import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Button } from "../../components/ui/Button";
import { Card } from "../../components/ui/card";
import { Download, MessageCircle, AlertTriangle, MessageSquare, User, Loader2, ChevronLeft, ChevronRight } from "lucide-react";
import { fetchChatRuleLogs } from "../../redux/Slices/ChatRulesSlice";
import { RootState, useAppDispatch } from "../../redux/store";
import { ChatRuleLog } from "../../modules/shared/model";
import { ChatRuleTypeEnum } from "../../modules/shared/model/chat-rules.enum";

const ChatReports = () => {
    const dispatch = useAppDispatch();
    const { chatRuleLogs, loading, error, logsPagination } = useSelector((state: RootState) => state.chatRules);

    // Fetch chat rule logs on component mount
    useEffect(() => {
        dispatch(fetchChatRuleLogs({ 
            page: 1, 
            limit: 10,
            sort: JSON.stringify([{ orderBy: "createdAt", order: "DESC" }])
        }));
    }, [dispatch]);

    // Handle page change
    const handlePageChange = (newPage: number) => {
        dispatch(fetchChatRuleLogs({ 
            page: newPage, 
            limit: logsPagination.limit,
            sort: JSON.stringify([{ orderBy: "createdAt", order: "DESC" }])
        }));
    };

    // Handle page size change
    const handlePageSizeChange = (newPageSize: number) => {
        dispatch(fetchChatRuleLogs({ 
            page: 1, 
            limit: newPageSize,
            sort: JSON.stringify([{ orderBy: "createdAt", order: "DESC" }])
        }));
    };

    // Calculate statistics from pagination data (use totalCount from backend)
    const totalViolations = logsPagination.totalCount || chatRuleLogs.length;
    const todayViolations = chatRuleLogs.filter(log => {
        const logDate = new Date(log.createdAt);
        const today = new Date();
        return logDate.toDateString() === today.toDateString();
    }).length;

    // Get rule type label
    const getRuleTypeLabel = (typeId: number) => {
        const labels = {
            [ChatRuleTypeEnum.No_Contact_Information]: "No Contact Information",
            [ChatRuleTypeEnum.Professional_Language]: "Professional Language",
            [ChatRuleTypeEnum.External_Platform_Links]: "External Platform Links",
            [ChatRuleTypeEnum.Spam_and_Repetitive_Messages_Filter]: "Spam and Repetitive Messages Filter",
            [ChatRuleTypeEnum.Sensitive_Content_Filter]: "Sensitive Content Filter",
            [ChatRuleTypeEnum.Chat_Message_Limit]: "Chat Message Limit",
            [ChatRuleTypeEnum.External_Deal_Detection]: "External Deal Detection",
            [ChatRuleTypeEnum.File_Attachment_Filter]: "File Attachment Filter",
            [ChatRuleTypeEnum.Role_Based_Contextual_Rules]: "Role-Based Contextual Rules"
        };
        return labels[typeId as keyof typeof labels] || "Unknown";
    };

    // Format date for display
    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
        
        if (diffInHours < 1) return "Just now";
        if (diffInHours < 24) return `${diffInHours} hour${diffInHours > 1 ? "s" : ""} ago`;
        return date.toLocaleDateString();
    };

    return (
        <div className="space-y-6" style={{ textAlign: "left" }}>
            <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
                <div>
                    <h2 className="text-lg font-semibold">Chat Reports</h2>
                    <p className="text-gray-600 mt-1">View chat analytics and violation reports</p>
                </div>
                <Button variant="outline" className="flex items-center gap-2 w-full sm:w-auto">
                    <Download className="h-4 w-4" />
                    Export Report
                </Button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <Card className="p-4 sm:p-6">
                    <div className="flex justify-between items-start">
                        <div className="space-y-1">
                            <p className="text-sm text-gray-500">Total Violations</p>
                            <p className="text-xl sm:text-2xl font-semibold">{logsPagination.totalCount || 0}</p>
                        </div>
                        <div className="p-2 bg-blue-100 rounded-lg">
                            <MessageCircle className="h-5 w-5 text-blue-600" />
                        </div>
                    </div>
                </Card>

                <Card className="p-4 sm:p-6">
                    <div className="flex justify-between items-start">
                        <div className="space-y-1">
                            <p className="text-sm text-gray-500">Current Page</p>
                            <p className="text-xl sm:text-2xl font-semibold">{logsPagination.page || 1}</p>
                        </div>
                        <div className="p-2 bg-purple-100 rounded-lg">
                            <MessageSquare className="h-5 w-5 text-purple-600" />
                        </div>
                    </div>
                </Card>

                <Card className="p-4 sm:p-6">
                    <div className="flex justify-between items-start">
                        <div className="space-y-1">
                            <p className="text-sm text-gray-500">Total Pages</p>
                            <p className="text-xl sm:text-2xl font-semibold">{logsPagination.totalPages || 0}</p>
                        </div>
                        <div className="p-2 bg-green-100 rounded-lg">
                            <MessageSquare className="h-5 w-5 text-green-600" />
                        </div>
                    </div>
                </Card>
            </div>

            <Card className="p-6">
                <h3 className="font-medium mb-4">Recent Rule Violations</h3>
                {loading ? (
                    <div className="flex items-center justify-center py-8">
                        <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
                        <span className="ml-2 text-gray-600">Loading violations...</span>
                    </div>
                ) : error ? (
                    <div className="flex items-center justify-center py-8">
                        <AlertTriangle className="h-6 w-6 text-red-500" />
                        <span className="ml-2 text-red-600">Error loading violations: {error}</span>
                    </div>
                ) : chatRuleLogs.length === 0 ? (
                    <div className="flex items-center justify-center py-8">
                        <MessageCircle className="h-6 w-6 text-gray-400" />
                        <span className="ml-2 text-gray-500">No violations found</span>
                    </div>
                ) : (
                    <>
                        <div className="space-y-3">
                            {chatRuleLogs.map((log: ChatRuleLog) => (
                                <div key={log.id} className="p-3 border rounded-lg flex flex-col sm:flex-row justify-between gap-2">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2">
                                            <AlertTriangle className="h-4 w-4 text-red-500" />
                                            <span className="font-medium">
                                                {log.chatRule?.type?.id ? getRuleTypeLabel(log.chatRule.type.id) : "Rule Violation"}
                                            </span>
                                        </div>
                                        <p className="text-sm text-gray-500 mt-1">
                                            Violation detected in conversation: 
                                            {log.conversation?.id && ` ${log.conversation.message}`}
                                        </p>
                                    </div>
                                    <div className="text-sm text-gray-500 flex items-center sm:flex-col sm:items-end whitespace-nowrap">
                                        <span>{formatDate(log.createdAt)}</span>
                                        <div className="flex items-center ml-4 sm:ml-0 sm:mt-1">
                                            <User className="h-3 w-3 mr-1" />
                                            <span>{log.user?.id ? `${log.user.firstName} ${log.user.lastName}` : "Unknown User"}</span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Pagination Controls */}
                        {chatRuleLogs.length > 0 && (
                            <div className="mt-6 pt-4 border-t">
                                <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                                    {/* Page Info */}
                                    <div className="text-sm text-gray-600">
                                        Showing {((logsPagination.page - 1) * logsPagination.limit) + 1} to{" "}
                                        {Math.min(logsPagination.page * logsPagination.limit, logsPagination.totalCount)} of{" "}
                                        {logsPagination.totalCount} violations
                                    </div>

                                    {/* Pagination Buttons */}
                                    <div className="flex items-center gap-2">
                                        {/* Previous Button */}
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => handlePageChange(logsPagination.page - 1)}
                                            disabled={!logsPagination.hasPreviousPage || logsPagination.page === 1 || loading}
                                            className="flex items-center gap-1"
                                        >
                                            <ChevronLeft className="h-4 w-4" />
                                            Previous
                                        </Button>

                                        {/* Page Numbers */}
                                        <div className="flex items-center gap-1">
                                            {(() => {
                                                const pages = [];
                                                const totalPages = logsPagination.totalPages;
                                                const current = logsPagination.page;

                                                // Show first page
                                                if (totalPages > 0) {
                                                    pages.push(
                                                        <Button
                                                            key={1}
                                                            size="sm"
                                                            variant={current === 1 ? "default" : "ghost"}
                                                            className="w-9 h-9"
                                                            onClick={() => handlePageChange(1)}
                                                            disabled={loading}
                                                        >
                                                            1
                                                        </Button>
                                                    );
                                                }

                                                // Show ellipsis if needed
                                                if (current > 3) {
                                                    pages.push(
                                                        <span key="ellipsis1" className="px-2 text-gray-500">...</span>
                                                    );
                                                }

                                                // Show pages around current page
                                                for (let i = Math.max(2, current - 1); i <= Math.min(totalPages - 1, current + 1); i++) {
                                                    if (i > 1 && i < totalPages) {
                                                        pages.push(
                                                            <Button
                                                                key={i}
                                                                size="sm"
                                                                variant={current === i ? "default" : "ghost"}
                                                                className="w-9 h-9"
                                                                onClick={() => handlePageChange(i)}
                                                                disabled={loading}
                                                            >
                                                                {i}
                                                            </Button>
                                                        );
                                                    }
                                                }

                                                // Show ellipsis if needed
                                                if (current < totalPages - 2) {
                                                    pages.push(
                                                        <span key="ellipsis2" className="px-2 text-gray-500">...</span>
                                                    );
                                                }

                                                // Show last page
                                                if (totalPages > 1) {
                                                    pages.push(
                                                        <Button
                                                            key={totalPages}
                                                            size="sm"
                                                            variant={current === totalPages ? "default" : "ghost"}
                                                            className="w-9 h-9"
                                                            onClick={() => handlePageChange(totalPages)}
                                                            disabled={loading}
                                                        >
                                                            {totalPages}
                                                        </Button>
                                                    );
                                                }

                                                return pages;
                                            })()}
                                        </div>

                                        {/* Next Button */}
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => handlePageChange(logsPagination.page + 1)}
                                            disabled={!logsPagination.hasNextPage || loading}
                                            className="flex items-center gap-1"
                                        >
                                            Next
                                            <ChevronRight className="h-4 w-4" />
                                        </Button>
                                    </div>
                                </div>

                                {/* Page Size Selector */}
                                <div className="flex items-center gap-2 mt-4 pt-4 border-t">
                                    <label className="text-sm text-gray-600">Items per page:</label>
                                    <select
                                        value={logsPagination.limit}
                                        onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                                        className="px-3 py-1 border rounded-lg text-sm"
                                        disabled={loading}
                                    >
                                        <option value={5}>5</option>
                                        <option value={10}>10</option>
                                        <option value={25}>25</option>
                                        <option value={50}>50</option>
                                        <option value={100}>100</option>
                                    </select>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </Card>
        </div>
    );
};

export default ChatReports;